import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { changePassword, ChangePasswordPayload } from '../api';
import './ChangePasswordView.css';

const ChangePasswordView: React.FC = () => {
  const location  = useLocation();
  const navigate  = useNavigate();
  const email = location.state?.email || location.state?.costCenterNumber; // support both old and new

  const [oldPassword,        setOldPassword]        = useState('');
  const [newPassword,        setNewPassword]        = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [showOld,            setShowOld]            = useState(false);
  const [showNew,            setShowNew]            = useState(false);
  const [showConfirm,        setShowConfirm]        = useState(false);
  const [error,              setError]              = useState<string | null>(null);
  const [successMessage,     setSuccessMessage]     = useState<string | null>(null);
  const [isLoading,          setIsLoading]          = useState(false);

  useEffect(() => {
    if (!email) navigate('/login', { replace: true });
  }, [email, navigate]);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(null);
    setSuccessMessage(null);

    if (newPassword.length < 6) {
      setError('New password must be at least 6 characters.');
      return;
    }
    if (newPassword !== confirmNewPassword) {
      setError('New passwords do not match.');
      return;
    }

    setIsLoading(true);
    const payload: ChangePasswordPayload = {
      email: email,
      old_password: oldPassword,
      new_password: newPassword,
    };

    try {
      const response = await changePassword(payload);
      if (response.success) {
        setSuccessMessage('Password updated. Redirecting to login…');
        setTimeout(() => navigate('/login'), 4000);
      } else {
        setError(response.message || 'Failed to change password.');
      }
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'An unexpected error occurred.');
    } finally {
      setIsLoading(false);
    }
  };

  if (!email) return null;

  return (
    <div className="cpv-wrap">
      <div className="cpv-card">

        {/* ── Header ── */}
        <div className="cpv-header">
          <span className="material-icons cpv-icon">lock_reset</span>
          <h2 className="cpv-title">Set Your Password</h2>
          <p className="cpv-subtitle">First login · {email}</p>
        </div>

        {/* ── Body ── */}
        <div className="cpv-body">
          <div className="cpv-info">
            Enter the temporary password you received, then choose a new one.
          </div>

          <form onSubmit={handleSubmit}>
            {/* Temp / old password */}
            <div className="cpv-field">
              <label className="cpv-label">Temporary Password</label>
              <div className="cpv-input-wrap">
                <input
                  type={showOld ? "text" : "password"}
                  className="cpv-input"
                  value={oldPassword}
                  onChange={e => setOldPassword(e.target.value)}
                  required
                  disabled={isLoading}
                  autoComplete="current-password"
                  placeholder="Enter your temp password"
                />
                <button type="button" className="cpv-eye" onClick={() => setShowOld(v => !v)} tabIndex={-1}>
                  <span className="material-icons">{showOld ? "visibility_off" : "visibility"}</span>
                </button>
              </div>
            </div>

            {/* New password */}
            <div className="cpv-field">
              <label className="cpv-label">New Password</label>
              <div className="cpv-input-wrap">
                <input
                  type={showNew ? "text" : "password"}
                  className="cpv-input"
                  value={newPassword}
                  onChange={e => setNewPassword(e.target.value)}
                  required
                  disabled={isLoading}
                  autoComplete="new-password"
                  placeholder="Min. 6 characters"
                />
                <button type="button" className="cpv-eye" onClick={() => setShowNew(v => !v)} tabIndex={-1}>
                  <span className="material-icons">{showNew ? "visibility_off" : "visibility"}</span>
                </button>
              </div>
            </div>

            {/* Confirm new password */}
            <div className="cpv-field">
              <label className="cpv-label">Confirm New Password</label>
              <div className="cpv-input-wrap">
                <input
                  type={showConfirm ? "text" : "password"}
                  className={`cpv-input${confirmNewPassword && confirmNewPassword !== newPassword ? " cpv-input--err" : ""}`}
                  value={confirmNewPassword}
                  onChange={e => setConfirmNewPassword(e.target.value)}
                  required
                  disabled={isLoading}
                  autoComplete="new-password"
                  placeholder="Re-enter new password"
                />
                <button type="button" className="cpv-eye" onClick={() => setShowConfirm(v => !v)} tabIndex={-1}>
                  <span className="material-icons">{showConfirm ? "visibility_off" : "visibility"}</span>
                </button>
              </div>
            </div>

            {error         && <div className="cpv-msg cpv-msg--err">{error}</div>}
            {successMessage && <div className="cpv-msg cpv-msg--ok">{successMessage}</div>}

            <button type="submit" className="cpv-btn" disabled={isLoading}>
              {isLoading ? 'Updating…' : 'Set Password'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ChangePasswordView;
